import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { BookingLanguage } from "@/types/booking";
import { languageMap } from "@/i18n";
import {
  type MarketCountry,
  DEFAULT_MARKET,
  isPackageTypeHiddenInMarket,
} from "@/config/market";
import { TabSwitcher, type Tab } from "@/components/ui/tab-switcher";
import { PersonPackageIcon } from "@/components/icons/PersonPackageIcon";
import { BloodLabsIcon } from "@/components/icons/BloodLabsIcon";
import { MembershipArrowIcon } from "@/components/icons/MembershipArrowIcon";

interface PackageTypeToggleProps {
  selectedPackageType: string;
  onToggle: (packageType: string) => void;
  language: BookingLanguage;
  market?: MarketCountry;
  /** Package types force-shown via `?force=mri`, overriding market hiding. */
  forcedPackageTypes?: readonly string[];
}

/** 29px circular slot used by every tab icon.
 *  Deselected: no tile at all — just the glyph on the track. The disc used to
 *    render in both states, which read as two selected tabs and left the
 *    active one to be told apart by fill alone.
 *  Selected: a per-tab accent disc.
 *    - Full-body: deeper light-blue fill
 *    - Blood: lighter red fill
 *    - Membership: inverted — dark neutral fill, glyph rendered white
 *  The 29px box is kept in both states so nothing shifts when switching. */
const IconCircle = ({
  variant,
  active,
  children,
}: {
  variant: "person" | "labs" | "arrow";
  active: boolean;
  children: React.ReactNode;
}) => {
  const selectedClass =
    variant === "person"
      ? "bg-light-blue-300 text-black"
      : variant === "labs"
        ? "bg-[#FBEAE0] text-neutral-900"
        : "bg-neutral-900 text-white";
  return (
    <span
      className={cn(
        "relative flex size-[29px] items-center justify-center rounded-full",
        active ? selectedClass : "text-neutral-700",
      )}
    >
      {children}
    </span>
  );
};

/** Labs icon is visually offset within its bounding box — shift it left
 *  (and a hair down) so it sits optically centered in the 29px circle. */
const LabsGlyphShift = ({ children }: { children: React.ReactNode }) => (
  <span
    aria-hidden="true"
    className="block"
    style={{ transform: "translate(0px, 0.5px)" }}
  >
    {children}
  </span>
);

export const PackageTypeToggle: React.FC<PackageTypeToggleProps> = ({
  selectedPackageType,
  onToggle,
  language,
  market = DEFAULT_MARKET,
  forcedPackageTypes = [],
}) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  /* The middle tab owns both the one-off baseline blood test and the
     recommended membership. Tab value stays "blood-only" — product
     filtering / clinic compat / URL state all key off it. */
  const bloodTabLabel = t("packageTypeToggle.bloodTracking");

  const tabs: Tab[] = [
    {
      value: "mri",
      label: t("packageTypeToggle.mri"),
      icon: (
        <IconCircle variant="person" active={selectedPackageType === "mri"}>
          <PersonPackageIcon size={22} />
        </IconCircle>
      ),
    },
    {
      value: "blood-only",
      label: bloodTabLabel,
      icon: (
        <IconCircle
          variant="labs"
          active={selectedPackageType === "blood-only"}
        >
          {/* Full opacity and full natural size: at 75% and 16px the tube
              glyph was the faintest thing in the control. */}
          <LabsGlyphShift>
            <BloodLabsIcon width={17.5} height={20} />
          </LabsGlyphShift>
        </IconCircle>
      ),
    },
    {
      value: "membership",
      label: t("packageTypeToggle.membership"),
      icon: (
        <IconCircle
          variant="arrow"
          active={selectedPackageType === "membership"}
        >
          <MembershipArrowIcon width={13.4} height={14.3} />
        </IconCircle>
      ),
    },
  ];

  // The membership tab is hidden everywhere (membership lives in the blood-only
  // tab); each market additionally hides the package types it doesn't offer
  // (Germany hides "mri", leaving only the blood test at beta launch).
  const visibleTabs = tabs.filter(
    (tab) =>
      tab.value !== "membership" &&
      !isPackageTypeHiddenInMarket(market, tab.value, forcedPackageTypes),
  );

  // A switcher with a single tab is just decoration — the NL/DE markets only
  // offer the blood test (AHD-8731).
  if (visibleTabs.length < 2) return null;

  return (
    <div className="flex justify-center mb-6 lg:mb-10">
      <TabSwitcher
        tabs={visibleTabs}
        activeTab={selectedPackageType}
        onChange={onToggle}
      />
    </div>
  );
};
